import style from "./style.scss";
import { useState, useRef, useEffect } from "preact/hooks";
import { h } from "preact";
import { mergeClasses } from "../helpers";

const AccordionItem = ({
  heading,
  preview,
  open = false,
  content,
  index,
  right,
  headingAs = null,
  disableContentPadding,
}) => {
  const [isOpen, setIsOpen] = useState(open);
  const [maxHeight, setMaxHeight] = useState("0px");
  const [hidden, setHidden] = useState("until-found");
  const [enableHeightTransition, setEnableHeightTransition] = useState(false);
  const useHeightTransition = useRef(false);
  const [enableAnimationFallback, setEnableAnimationFallback] = useState(false);

  const panelRef = useRef(null);
  const uniqueId = useRef(`panel-${Math.random().toString(36).substr(2, 9)}`);
  //const uniqueId = useRef(`panel-${crypto.randomUUID()}`); // Prevents collisions but is slower
  const eventRefs = useRef({
    // Use a ref to store the event listeners to ensure proper cleanup
    beforeMatch: null,
    transitionEnd: null,
  });

  useEffect(() => {
    if (panelRef.current && "onbeforematch" in document) {
      // Handler for "Find in Page" automatic expansion
      const handleBeforeMatch = () => {
        setEnableHeightTransition(false);
        setMaxHeight("none");
        useHeightTransition.current = false;
        setIsOpen(true);
      };
      eventRefs.current.beforeMatch = handleBeforeMatch;
      panelRef.current.addEventListener("beforematch", handleBeforeMatch);
    } else {
      // Use animation when a browser doesn't support onbeforematch
      setEnableAnimationFallback(true);
    }

    return () => {
      //Clean up the event listeners when component unmounts
      if (panelRef.current) {
        if (eventRefs.current.transitionEnd) {
          panelRef.current.removeEventListener(
            "transitionend",
            eventRefs.current.transitionEnd
          );
        }
        if (eventRefs.current.beforeMatch) {
          panelRef.current.removeEventListener(
            "beforematch",
            eventRefs.current.beforeMatch
          );
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!panelRef.current) return;

    if (useHeightTransition.current && typeof TransitionEvent !== "undefined") {
      // Toggle using a transition
      setHidden(undefined); // Unhide to capture scrollHeight
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const scrollHeight = panelRef.current.scrollHeight;

          // Always remove previous listener before adding a new one to prevent duplicates
          panelRef.current.removeEventListener(
            "transitionend",
            eventRefs.current.transitionEnd
          );

          setMaxHeight(`${isOpen ? 0 : scrollHeight}px`); // Might be able to use: panelRef.current.style.maxHeight = `${isOpen ? 0 : scrollHeight}px`;

          const handleTransitionEnd = (e) => {
            if (e.propertyName === "max-height") {
              panelRef.current.removeEventListener(
                "transitionend",
                handleTransitionEnd
              );
              eventRefs.current.transitionEnd = null; // Clear the ref

              setHidden(isOpen ? undefined : "until-found");
              setMaxHeight(isOpen ? "none" : "0px");
            }
          };

          eventRefs.current.transitionEnd = handleTransitionEnd;
          panelRef.current.addEventListener(
            "transitionend",
            handleTransitionEnd
          );

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // The max-height CSS transition should be ready for changes
              setMaxHeight(`${isOpen ? scrollHeight : 0}px`);
            });
          });
        });
      });
    } else {
      // Toggle without a transition
      setMaxHeight(isOpen ? "none" : "0px");
      setHidden(isOpen ? undefined : "until-found");
    }
  }, [isOpen]);

  const toggleAccordion = () => {
    useHeightTransition.current = true;
    setEnableHeightTransition(true);
    setIsOpen(!isOpen);
  };

  const Tag = headingAs || "div";

  return (
    <div class={style.accordionItem}>
      <Tag class={style.headingWrapper}>
        <button
          class={mergeClasses([
            style.accordionHeader,
            !right ? style.buttonLeft : null,
          ])}
          aria-expanded={isOpen}
          aria-controls={uniqueId.current}
          onClick={toggleAccordion}
        >
          <span class={style.heading}>
            {preview ? (
              preview
            ) : (
              <span class={style.headingText}>{heading}</span>
            )}
          </span>
          <span class={style.toggle} />
        </button>
      </Tag>
      <div
        class={mergeClasses([
          style.accordionPanel,
          enableHeightTransition ? style.transition : null,
          enableAnimationFallback ? style.animateFallback : null,
        ])}
        id={uniqueId.current}
        hidden={hidden}
        aria-hidden={!isOpen}
        style={{ maxHeight }}
        ref={panelRef}
      >
        <div
          class={mergeClasses([
            style.contentWrapper,
            disableContentPadding ? style.disableContentPadding : null,
          ])}
        >
          {content}
        </div>
      </div>
    </div>
  );
};

const Accordion = ({
  items,
  right = false,
  disableContentPadding = false,
  headingsAs = null,
}) => {
  return (
    <div class={style.accordion}>
      {items.map((item, index) => (
        <AccordionItem
          right={right}
          key={index}
          index={index}
          heading={item.heading}
          headingAs={item.headingsAs || headingsAs}
          preview={item.preview}
          content={item.content}
          open={item.open}
          disableContentPadding={disableContentPadding}
        />
      ))}
    </div>
  );
};

export default Accordion;
